import type { LucideIcon } from 'lucide-react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface StatCardProps {
  readonly label: string;
  readonly value: string | number;
  readonly delta?: string;
  readonly trend?: 'up' | 'down' | 'neutral';
  readonly trendColor?: string;
  readonly icon?: LucideIcon;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly colorOverride?: string;
}

const TREND_CONFIG = {
  up: { icon: TrendingUp, color: 'text-compliance-low' },
  down: { icon: TrendingDown, color: 'text-compliance-critical' },
  neutral: { icon: Minus, color: 'text-muted-foreground' },
} as const;

export function StatCard({
  label,
  value,
  delta,
  trend,
  trendColor,
  icon: Icon,
  className,
  onClick,
  colorOverride,
}: StatCardProps) {
  const trendConfig = trend ? TREND_CONFIG[trend] : null;
  const TrendIcon = trendConfig?.icon;
  const resolvedTrendColor = trendColor ?? trendConfig?.color;

  return (
    <Card
      className={cn(
        onClick && 'cursor-pointer transition-colors hover:bg-muted/30',
        className,
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{label}</p>
          {Icon && (
            <Icon className={cn('h-4 w-4', colorOverride ?? 'text-muted-foreground')} />
          )}
        </div>
        <div className="mt-2 flex items-end gap-2">
          <p className={cn('text-2xl font-bold', colorOverride ?? 'text-foreground')}>
            {value}
          </p>
          {delta && trendConfig && TrendIcon && (
            <span
              className={cn(
                'flex items-center gap-0.5 text-xs font-medium',
                resolvedTrendColor,
              )}
            >
              <TrendIcon className="h-3 w-3" />
              {delta}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
